/**
 * Custom hook for polling job status
 */

import { useState, useEffect, useRef } from 'react';
import { JobStatusResponse } from '../types';
import { getJobStatus } from '../services/api';

const POLL_INTERVAL_MS = 15000; // 15 seconds

interface UseJobPollingOptions {
  jobId: string | null;
  onStatusChange?: (status: JobStatusResponse) => void;
  onError?: (error: Error) => void;
}

interface UseJobPollingResult {
  jobStatus: JobStatusResponse | null;
  isPolling: boolean;
  error: string | null;
  stopPolling: () => void;
  startPolling: () => void;
}

export function useJobPolling({
  jobId,
  onStatusChange,
  onError,
}: UseJobPollingOptions): UseJobPollingResult {
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  const stopPolling = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  };

  const startPolling = () => {
    if (!jobId) return;
    setIsPolling(true);
    setError(null);
  };

  const pollStatus = async () => {
    if (!jobId) return;

    try {
      const status = await getJobStatus(jobId);

      if (!isMountedRef.current) return;

      setJobStatus(status);
      setError(null);

      if (onStatusChange) {
        onStatusChange(status);
      }

      // Stop polling if job is in terminal state
      if (status.status === 'COMPLETED' || status.status === 'ERROR') {
        stopPolling();
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch job status';
      setError(errorMessage);

      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMessage));
      }

      // Stop polling on 404 (job not found) or other permanent errors
      // Continue polling only on temporary network issues
      if (errorMessage.includes('Job not found') || errorMessage.includes('404')) {
        console.error('Job not found, stopping polling:', err);
        stopPolling();
      } else {
        // Log temporary errors but continue polling
        console.error('Error polling job status (will retry):', err);
      }
    }
  };

  // Set up polling interval
  useEffect(() => {
    if (!isPolling || !jobId) {
      return;
    }

    // Poll immediately
    pollStatus();

    // Then poll every POLL_INTERVAL_MS
    intervalRef.current = window.setInterval(pollStatus, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, jobId]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, []);

  // Auto-start polling when jobId changes
  useEffect(() => {
    if (jobId) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [jobId]);

  return {
    jobStatus,
    isPolling,
    error,
    stopPolling,
    startPolling,
  };
}
